import isEmpty from 'lodash/isEmpty';

import { FetchError } from './fetch.error';
import { publishNdStateEvent } from './fetch.events';
import { buildQueryString, getContentType, isValidData, serializeData } from './fetch.utils';
import { sampleServerClock } from './server.clock';

interface FetchOptions extends Omit<RequestInit, 'body' | 'headers'> {
    url: string;
    timeout?: number;
    cancellableKey?: string;
    contentType?: string;
    accept?: string;
    headers?: Record<string, string>;
    data?: unknown;
    query?: Record<string, unknown>;
    withCredentials?: boolean;
    /**
     * Vouches for this request's response as a reading of the TOKEN ISSUER's clock, letting its `Date` header
     * become the server clock that token expiry is measured against.
     *
     * Off by default, and correct only on a request whose response carries a bearer token — today the four
     * `authentication/v1` calls and the legacy `auth/` ticket exchange in `services.ts`. `fetchShim` is the app's
     * one HTTP door and it opens onto origins this app does not control (`sendRequest` swaps the base URL outright
     * for any service with a `cdnProfile`), so treating every `Date` as the server's would let an unrelated
     * machine's idea of the time decide when a good token gets refreshed. See `server.clock`.
     *
     * Setting it also defaults the request to `cache: 'no-store'`, because a sample is only worth taking off a
     * response that came from the network — see where that is applied below.
     */
    trustServerClock?: boolean;
}

const abortControllers = new Map<string, AbortController>();

async function fetchShim<TResult>(options: FetchOptions): Promise<TResult> {
    const {
        url,
        timeout,
        cancellableKey,
        contentType,
        accept,
        data,
        query = {},
        withCredentials = false,
        trustServerClock = false,
        ...rest
    } = options;

    const headersContentType = rest.headers ? rest.headers['Content-Type'] : undefined;
    const defaultContentType = getContentType(data, contentType || headersContentType);
    const headers = {
        ...rest.headers,
        ...(contentType ? { 'Content-Type': defaultContentType } : {}),
        ...(accept ? { Accept: accept } : {}),
    };
    const body = isValidData(data) ? serializeData(data, defaultContentType) : null;
    const controller = new AbortController();
    if (cancellableKey) {
        abortControllers.get(cancellableKey)?.abort();
        abortControllers.set(cancellableKey, controller);
    }
    const init: RequestInit = { ...rest, body, headers, signal: controller.signal };
    const queryString = isEmpty(query) ? null : buildQueryString(query);
    const finalUrl = queryString ? `${url}?${queryString}` : url;

    if (withCredentials) {
        init.credentials = 'include';
    }

    // A sampled response has to come off the network, not out of a cache. `server.clock` can reject a replayed
    // `Date` once it has a reading to compare against, but it has nothing to compare the FIRST one to and no way to
    // recognize a browser cache hit — that replays the stored headers, original `Date` and all, and adds no `Age`
    // for the `Age` guard to catch. A first sample two hours stale is the worst case the whole module exists to
    // avoid: it puts the estimate behind the real server, so tokens read as longer-lived than they are and requests
    // go out against ones the server has already retired.
    //
    // `no-store` is what makes accepting that first sample sound, and it is the right cache mode for these requests
    // on its own account: every one of them mints a bearer token, and a cached token response is stale by
    // definition. Of today's five, the four `authentication/v1` calls are POSTs and so were never cacheable; the
    // legacy `auth/` ticket exchange is a GET, which is the live exposure, and nothing stops the next trusted
    // request from being one too. Applied here rather than at the five call sites for that reason — the guarantee
    // belongs to the flag, not to whoever remembers to pair it.
    //
    // An explicit `cache` from the caller still wins: this is a default, not an override. Note also what it cannot
    // promise — a service worker may answer from its own cache whatever the request asks for. None of the auth
    // endpoints are in a worker's scope today, and `Age`/`isStaleSample` still screen what arrives.
    if (trustServerClock && !init.cache) {
        init.cache = 'no-store';
    }

    const timeoutPromise = timeout
        ? new Promise<never>((_, reject) =>
              setTimeout(() => {
                  controller.abort();
                  const statusText = 'Request timed out';
                  const error = new FetchError(408, statusText, Promise.resolve({ Message: statusText, ResourceKey: null }));

                  reject(error);
              }, timeout),
          )
        : null;

    const requestPromise = fetch(finalUrl, init).then(response => {
        // Gated, not unconditional: only the caller knows whether this response came from the service that stamps
        // the token's `exp`, and only that machine's `Date` is the clock expiry may be measured against — see
        // `trustServerClock` above.
        //
        // Placed before the status check on purpose: a rejected AUTH response carries just as good a `Date`, and
        // the moment the token clock is most worth correcting is when requests are coming back rejected.
        //
        // Optional chaining because this is a passive side effect sitting on the critical path of every request in
        // the app — a response shape without `headers` (a test double, a polyfill) must degrade to "no sample",
        // never take the request down with it.
        if (trustServerClock) {
            sampleServerClock(response.headers?.get('date'), response.headers?.get('age'));
        }

        if (!response.ok) {
            throw new FetchError(response.status, response.statusText, response.json());
        }

        publishNdStateEvent(response.headers.get('ndstate'));

        const contentType = response.headers.get('content-type');

        if (contentType?.includes('application/json')) {
            return response.json();
        } else {
            return response.text();
        }
    });

    try {
        const result = await Promise.race([requestPromise, ...(timeoutPromise ? [timeoutPromise] : [])]);

        if (cancellableKey) {
            abortControllers.delete(cancellableKey);
        }

        return result;
    } catch (e) {
        if (cancellableKey) {
            abortControllers.delete(cancellableKey);
        }

        if (e instanceof FetchError) {
            throw await e.getHttpError();
        } else {
            throw e;
        }
    }
}

export { FetchOptions, fetchShim };
